import React from 'react';
import { styled } from '@this/constants/themes';

import { reportError } from '@this/lib/bugsnag';
import { StyledReserveWarningAlert, StyledAlertTitle } from '@this/components/shared/reserve_ui_components';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Link } from '@this/components/shared/ui/navigations/link';
import type Hotel from '@this/src/domain/hotel/hotel2';
import { digits } from '@this/src/util';
import Text from '../../../shared/text/text';
import StayPlanWarning from '../../select/shared/stay_plan_warning';

type Props = {
  hotel: Hotel | null | undefined;
  isPackage: boolean;
  hotelPrice: number;
  itemTestIdPrefix: string;
  peoplenum: number;
  roomnum: number | undefined;
};

const ReserveConfirmHotelTr: React.FC<Props> = ({
  hotel,
  isPackage,
  hotelPrice,
  itemTestIdPrefix,
  peoplenum,
  roomnum
}) => {
  try {
    if (!hotel) return <span>-</span>;

    return (
      <>
        <div data-testid={`${itemTestIdPrefix}-name`}>
          <Text text={hotel.name} />
        </div>
        <TextDetail>
          <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-date-range-value`}>
            {hotel.checkinDate?.format('YYYY-MM-DD')} 〜 {hotel.checkoutDate?.format('YYYY-MM-DD')}
          </span>
          <br />
          {isPackage ? (
            <span data-testid={`${itemTestIdPrefix}-occupancy-value`}>{`${peoplenum}名/${roomnum}室`}</span>
          ) : (
            <div className="flex">
              <span data-wovn-ignore>{digits(hotelPrice)}</span>円({peoplenum}名/{roomnum}室)
            </div>
          )}
          {hotel.mandatoryTax && hotel.mandatoryTax > 0 ? (
            <p data-wovn-ignore>{digits(hotel.mandatoryTax)}円(現地徴収料)</p>
          ) : null}
          <p data-testid={`${itemTestIdPrefix}-stay-row`}>
            {hotel.stayDays}泊{hotel.days}日
          </p>
          <p data-testid={`${itemTestIdPrefix}-checkin-row`}>
            <span data-testid={`${itemTestIdPrefix}-checkin-label`}>チェックイン　：</span>
            <span data-testid={`${itemTestIdPrefix}-checkin-value`}>{hotel.checkinText}</span>
          </p>
          <p data-testid={`${itemTestIdPrefix}-checkout-row`}>
            <span data-testid={`${itemTestIdPrefix}-checkout-label`}>チェックアウト：</span>
            <span data-testid={`${itemTestIdPrefix}-checkout-value`}>{hotel.checkoutText}</span>
          </p>
          <p data-testid={`${itemTestIdPrefix}-breakfast-row`}>
            <span data-testid={`${itemTestIdPrefix}-breakfast-label`}>朝食：</span>
            <span data-testid={`${itemTestIdPrefix}-breakfast-value`}>
              {hotel.breakfast == null ? 'お問合せください' : hotel.breakfast ? '有り' : '無し'}
            </span>
          </p>
          <p data-testid={`${itemTestIdPrefix}-plan-name-row`}>
            <span data-testid={`${itemTestIdPrefix}-plan-name-label`}>宿泊プラン：</span>
            <span data-testid={`${itemTestIdPrefix}-plan-name-value`}>{hotel.planName || '-'}</span>
          </p>
          {hotel.selectedPlanRestricted && (
            <StayPlanWarningWrapper>
              <StayPlanWarning variant="text" restricted={hotel.selectedPlanRestricted} />
            </StayPlanWarningWrapper>
          )}
          {hotel.detailPath && (
            <p data-testid={`${itemTestIdPrefix}-detail-link-row`}>
              <span data-testid={`${itemTestIdPrefix}-detail-link-label`}>宿泊詳細情報：</span>
              <Link
                href={hotel.detailPath}
                isExternal
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`${itemTestIdPrefix}-detail-link`}
              >
                詳細はコチラ
              </Link>
            </p>
          )}
          {hotel.roomType && (
            <p data-testid={`${itemTestIdPrefix}-room-type-row`}>
              <span data-testid={`${itemTestIdPrefix}-room-type-label`}>部屋タイプ：</span>
              <span data-testid={`${itemTestIdPrefix}-room-type-value`}>{hotel.roomType}</span>
            </p>
          )}
          <p data-testid={`${itemTestIdPrefix}-bed-type-row`}>
            <span data-testid={`${itemTestIdPrefix}-bed-type-label`}>ベッドタイプ：</span>
            <span data-testid={`${itemTestIdPrefix}-bed-type-value`}>{hotel.displayBedType}</span>
          </p>
          {hotel.hasMultipleBedTypes && (
            <div style={{ margin: '8px 0' }}>
              <StyledReserveWarningAlert icon={<ErrorOutlineIcon />} variant="filled" severity="error">
                <StyledAlertTitle>ベッドタイプについて</StyledAlertTitle>
                <div>
                  <p style={{ margin: '4px 0' }}>{hotel.bedTypeNoticeMessage}</p>
                </div>
              </StyledReserveWarningAlert>
            </div>
          )}
          {!isPackage && (
            <p>
              ホテルの返金：
              {hotel.refundable == null ? (
                'お問合せください'
              ) : hotel.refundable ? (
                '可'
              ) : (
                <ReserveConfirmCancelStatus>不可</ReserveConfirmCancelStatus>
              )}
            </p>
          )}
        </TextDetail>
        {hotel.averagePriceDetailArray.map((d, i) => (
          <PriceDetail key={i} data-testid={`${itemTestIdPrefix}-price-detail-${i}-row`}>
            <p data-testid={`${itemTestIdPrefix}-price-detail-${i}-title`}>{d.title}</p>
            <p data-testid={`${itemTestIdPrefix}-price-detail-${i}-value`}>{d.price}</p>
          </PriceDetail>
        ))}
      </>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const PriceDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 100px;
`;

const ReserveConfirmCancelStatus = styled.span`
  font-weight: bold;
  color: #959595;
  padding: 6px 10px 10px 10px;
`;

const TextDetail = styled.div`
  font-size: 0.9em;
  padding-left: 0.9em;
  font-weight: normal;
`;

const StayPlanWarningWrapper = styled.div`
  margin: 2px 0;
`;

export default ReserveConfirmHotelTr;
